import { apiRoutes } from '@socialfy/api-contract';
import { type ReactNode, useEffect, useState } from 'react';
import { useActivity } from '@/api/activity';
import { useApiQuery } from '@/api/query';
import { useSession } from '@/api/session';
import { SkipLink } from '@/shell/ui/visually-hidden';
import { ErrorCard } from '@/states/error-card';
import { LoadingBlock } from '@/states/loading-block';
import { OfflineBar } from '@/states/offline-bar';
import { ActivityStrip } from './activity-strip';
import { BottomBar } from './bottom-bar';
import { DemoBanner } from './demo-banner';
import { ReminderBanner } from './reminder-banner';
import { CompactHeader, TopBar } from './top-bar';
import { TOP_BAR_QUERY, useMediaQuery } from './use-media-query';

/**
 * So lange (ms) hält die Hülle den Seiteninhalt höchstens zurück, bis die Erinnerung für das Band feststeht. Antwortet der Dienst
 * nicht in dieser Zeit, erscheint der Inhalt ohne Band; ein später eintreffendes Band schiebt ihn dann nach unten (selten, nur
 * bei sehr langsamem Dienst).
 */
export const REMINDER_WAIT_MS = 2000;

/**
 * Seitenhülle: Sprunglink, Navigation (oben ab 640 px, sonst unten), Bänder in fester Reihenfolge (Demo, ohne
 * Verbindung, Erinnerung, „Was gerade passiert“) und der Inhalt in `main#inhalt`. Solange die Sitzung lädt (oder scheitert), gibt es
 * nur die schmale Kopfzeile und ein Skelett bzw. eine Fehlerkarte: kein Aufblitzen einer Navigation für
 * Konten ohne Zugang (401 leitet zur Anmeldung, 403 zu „Kein Zugang“).
 *
 * Layoutstabilität: Das Erinnerungsband (bis ~135 px hoch auf dem Handy) wird erst nach einer eigenen Abfrage bekannt. Auf langsamen
 * Geräten erschien der Inhalt schon vorher und wurde vom Band nach unten geschoben (CLS 0,14 bei 6-facher CPU-Drosselung). Der
 * Inhalt wird deshalb erst gezeigt, wenn die Abfragen von Band und Streifen entschieden sind (Treffer, leer oder Fehler), höchstens `REMINDER_WAIT_MS` lang;
 * dieselbe Abfrage teilt sich das Band über den Zwischenspeicher, es gibt keine zweite Anfrage.
 */
export function AppShell({ pathname, children }: { pathname: string; children: ReactNode }) {
  const wide = useMediaQuery(TOP_BAR_QUERY);
  const session = useSession();
  const ready = session.status === 'ready';
  const reminders = useApiQuery(
    apiRoutes.core.listReminders,
    { query: { limit: 1 } },
    { enabled: ready },
  );
  // „Was gerade passiert“: dieselbe Abfrage wie im Streifen (geteilter Zwischenspeicher)
  const activity = useActivity();
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);
  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    const timer = setTimeout(() => setWaitedLongEnough(true), REMINDER_WAIT_MS);
    return () => clearTimeout(timer);
  }, [ready]);
  const bannerDecided = (!reminders.isPending && !activity.isPending) || waitedLongEnough;
  return (
    <div className="flex min-h-dvh flex-col bg-surface-page text-ink">
      <SkipLink />
      {ready && wide ? <TopBar pathname={pathname} /> : <CompactHeader />}
      {ready ? (
        <>
          <DemoBanner />
          <OfflineBar />
          <ReminderBanner />
          <ActivityStrip />
        </>
      ) : null}
      <main
        id="inhalt"
        tabIndex={-1}
        className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 outline-none lg:px-6"
      >
        {ready && bannerDecided ? children : null}
        {session.status === 'loading' ? <LoadingBlock height={240} /> : null}
        {session.status === 'error' ? (
          <ErrorCard error={session.error} onRetry={() => window.location.reload()} />
        ) : null}
      </main>
      {ready && !wide ? <BottomBar pathname={pathname} /> : null}
    </div>
  );
}
